import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, ShieldCheck, ScanSearch, Loader2, EyeOff } from 'lucide-react';
import { Button } from '../ui/button';
import EditableVisualBlocks, { countFlags } from './EditableVisualBlocks';
import { API_BASE } from '../../config/api';

const API = `${API_BASE}/api`;

export const recheckVisuals = async (courseId, index, blocks, token) => {
  const res = await axios.post(`${API}/ai/course/${courseId}/sections/${index}/visual-blocks/recheck`, { blocks },
    { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 });
  return res.data.blocks;
};

// Edit-mode editor for a section's saved visuals: inline edits, NAVI re-check, and "Mark reviewed" (publishes to learners).
export default function SavedVisualsEditor({ courseId, sectionIndex, token, blocks, review, onSaved }) {
  const [draft, setDraft] = useState(blocks || []);
  const [busy, setBusy] = useState('');
  useEffect(() => { setDraft(blocks || []); }, [blocks]);
  if (!blocks?.length) return null;
  const pending = review?.status === 'pending';
  const dirty = JSON.stringify(draft) !== JSON.stringify(blocks);
  const flags = countFlags(draft);

  const save = async (markReviewed) => {
    setBusy(markReviewed ? 'review' : 'save');
    try {
      const res = await axios.put(`${API}/ai/course/${courseId}/sections/${sectionIndex}/visual-blocks`,
        { blocks: draft, mark_reviewed: markReviewed }, { headers: { Authorization: `Bearer ${token}` } });
      onSaved(res.data.blocks, res.data.visual_review);
      toast.success(markReviewed ? 'Visuals reviewed and now visible to learners' : 'Changes saved. Mark as reviewed to show learners');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save visuals');
    } finally {
      setBusy('');
    }
  };

  const recheck = async () => {
    setBusy('recheck');
    try {
      const checked = await recheckVisuals(courseId, sectionIndex, draft, token);
      setDraft(checked);
      const n = countFlags(checked);
      if (n) toast.warning(`${n} item${n === 1 ? '' : 's'} still not found in the section text`);
      else toast.success('NAVI found every item in the section text');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'NAVI could not fact-check right now');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="my-6 rounded-2xl border border-zinc-800 bg-zinc-900/40 p-4" data-testid={`saved-visuals-editor-${sectionIndex}`}>
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <h4 className="text-white font-semibold mr-auto">Section visuals</h4>
        {pending ? (
          <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-amber-400/15 text-amber-300 border border-amber-400/30" data-testid={`visuals-status-pending-${sectionIndex}`}><EyeOff className="h-3 w-3" />Awaiting review: hidden from learners</span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-teal-500/15 text-teal-300 border border-teal-500/30" data-testid={`visuals-status-reviewed-${sectionIndex}`}><ShieldCheck className="h-3 w-3" />Reviewed: visible to learners</span>
        )}
      </div>
      <EditableVisualBlocks blocks={draft} onChange={setDraft} testIdPrefix={`saved-visual-${sectionIndex}`} />
      <div className="flex flex-wrap gap-2 justify-end">
        <Button type="button" size="sm" variant="outline" onClick={recheck} disabled={!!busy || !draft.length} className="border-zinc-700 text-zinc-300" data-testid={`visuals-recheck-btn-${sectionIndex}`}>
          {busy === 'recheck' ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ScanSearch className="h-4 w-4 mr-1.5" />}Re-check with NAVI
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={() => save(false)} disabled={!!busy || !dirty} className="border-zinc-700 text-zinc-200" data-testid={`visuals-save-btn-${sectionIndex}`}>
          {busy === 'save' ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}Save changes
        </Button>
        {(pending || dirty) && (
          <Button type="button" size="sm" onClick={() => save(true)} disabled={!!busy} className="bg-teal-600 hover:bg-teal-500 text-white" data-testid={`visuals-mark-reviewed-btn-${sectionIndex}`}
            title={flags ? 'Some items are still flagged. Marking reviewed confirms you have checked them' : undefined}>
            {busy === 'review' ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-1.5" />}Mark reviewed{flags ? ` (${flags} flagged)` : ''}
          </Button>
        )}
      </div>
    </div>
  );
}
